import { useCallback, useEffect, useRef, useState } from "react";

interface DebouncedFunction<T extends (...args: any[]) => any> {
    (...args: Parameters<T>): void;
    cancel: () => void;
}

function debounce<T extends (...args: any[]) => any>(func: T, delay: number): DebouncedFunction<T> {
    let timeoutId: ReturnType<typeof setTimeout>;

    const debouncedFunction = function (this: any, ...args: Parameters<T>) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    } as DebouncedFunction<T>;

    debouncedFunction.cancel = function () {
        clearTimeout(timeoutId);
    };

    return debouncedFunction;
}

export function useDebounce<T extends (...args: any[]) => any>(callback: T, delay: number): (...args: Parameters<T>) => void {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const debouncedRef = useRef<DebouncedFunction<T>>();

    if (!debouncedRef.current) {
        debouncedRef.current = debounce((...args: Parameters<T>) => callbackRef.current(...args), delay);
    }

    useEffect(() => () => debouncedRef.current?.cancel(), []);

    return useCallback((...args: Parameters<T>) => {
        debouncedRef.current!(...args);
    }, []);
}

export function useDelayedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}
